import React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { backDecision, backLabelText, previousLocation, sectionBackLabel, useRecordPageLocation } from '@/lib/pageBack'

export interface PageHeaderProps {
  title: React.ReactNode
  /** Short context shown beside the title ("25 people · 10 companies"). */
  subtitle?: React.ReactNode
  /** @deprecated The v2 header has no kicker; kept so older callers compile. */
  eyebrow?: React.ReactNode
  /** Right-aligned action pills. Buttons inside are sized to the bar. */
  actions?: React.ReactNode
  /** Breadcrumb back to a parent page, e.g. Customers / Janet Burns. Pass
   *  `onClick` instead of `to` when the parent is the same route in another
   *  state (the Workflows list behind its editor). `to` is the fixed parent
   *  used when there is no in-app history. `label` is the parent's name.
   *  `besideTitle` keeps the control on the title's row (pages that had no
   *  crumb). `followHistory` returns to the page you opened this from. */
  back?: {
    to?: string
    onClick?: () => void
    label: React.ReactNode
    besideTitle?: boolean
    followHistory?: boolean
  }
  /** Cover the header while a drawer is open so this control isn't focusable. */
  inert?: boolean
  className?: string
}

/**
 * 44px hit area on the crumb itself. No vertical negative margin: on a phone
 * the existing crumb is already a 44px row, and pulling it up moved the title
 * and let the tap box cover the H1. Beside the title, the extra height goes
 * upward into the header padding (`-mt-3`) and the box ends at the title.
 */
const BACK_HIT = 'inline-flex w-fit items-center overflow-visible h-11 min-h-11 min-w-11 leading-none pl-[6px] pr-[14px] -ml-[10px]'
const BACK_HIT_BESIDE = `${BACK_HIT} -mt-3 self-start`

export function PageBackControl({ to, label, onClick, besideTitle, followHistory }: {
  to?: string
  label: React.ReactNode
  onClick?: () => void
  besideTitle?: boolean
  followHistory?: boolean
}) {
  const location = useLocation()
  const navigate = useNavigate()
  useRecordPageLocation()
  const previous = previousLocation()
  const useHistory = followHistory && location.key !== 'default' && previous != null
  const shownLabel = useHistory && typeof label === 'string' ? sectionBackLabel(previous, label) : label
  const name = typeof shownLabel === 'string' ? backLabelText(shownLabel) : null
  const aria = name?.aria ?? 'Back'
  const shown = name ? (name.iconOnly ? null : name.text) : shownLabel
  const go = (event: React.MouseEvent) => {
    if (onClick && !to) {
      event.preventDefault()
      onClick()
      return
    }
    if (!to && !useHistory) return
    event.preventDefault()
    if (useHistory || (to && backDecision({ locationKey: location.key, previous, parent: to }) === 'history')) navigate(-1)
    else if (to) navigate(to, { replace: true })
  }
  const className = cn('ds-page-h-crumb', besideTitle ? BACK_HIT_BESIDE : BACK_HIT)
  const inner = (
    <>
      <ChevronLeft aria-hidden className="h-[22px] w-[22px] shrink-0" />
      {shown != null && shown !== '' && <span>{shown}</span>}
    </>
  )
  if (to) {
    return <Link to={to} onClick={go} className={className} aria-label={aria} data-testid="page-back">{inner}</Link>
  }
  return <button type="button" onClick={go} className={className} aria-label={aria} data-testid="page-back">{inner}</button>
}

/**
 * PageHeader — the one header every page uses.
 *
 * Desktop: a 60px sticky bar (16px title, muted subtitle, compact pill
 * actions) that runs edge to edge across the page and stays put while the
 * page scrolls, so the page's main action is always in reach.
 * Phones: a large title in the page flow — the fixed app bar above it
 * already owns the top of the screen.
 *
 * Place it first inside `.page-container`; it reads the container's padding
 * variables to break out to the edges.
 */
export function PageHeader({ title, subtitle, actions, back, inert, className }: PageHeaderProps) {
  useRecordPageLocation()
  return (
    <header className={cn('ds-page-h', className)} inert={inert || undefined}>
      <div className="ds-page-h-t">
        {back?.besideTitle ? (
          <div className="flex w-full min-w-0 items-start gap-1">
            <PageBackControl to={back.to} label={back.label} onClick={back.onClick} besideTitle followHistory={back.followHistory} />
            <h1 className="min-w-0">{title}</h1>
          </div>
        ) : (
          <>
            {back && (
              <>
                <PageBackControl to={back.to} label={back.label} onClick={back.onClick} followHistory={back.followHistory} />
                <span className="ds-page-h-sep" aria-hidden>/</span>
              </>
            )}
            <h1>{title}</h1>
          </>
        )}
        {subtitle && <span className="ds-page-h-sub">{subtitle}</span>}
      </div>
      {actions && <div className="ds-page-h-acts">{actions}</div>}
    </header>
  )
}
